import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router';
import { ErrorState, LoadingState } from '@/components/common';
import { useCurrentUser } from '@/contexts';
import { useConversations, useUser } from '@/hooks';
import type { UserProfile } from '@/types';
import { normalizePhotos } from '@/utils';
import { ProfilePage } from './ProfilePage';

type ProfileLocationState = {
  user?: UserProfile;
};

function mergeProfile(
  fetched: UserProfile | null,
  preview: UserProfile | null
): UserProfile | null {
  if (!fetched && !preview) return null;
  if (!fetched) return preview;
  if (!preview) return fetched;

  const fetchedPhotos = normalizePhotos(fetched.photos);
  const previewPhotos = normalizePhotos(preview.photos);

  return {
    ...preview,
    ...fetched,
    // Prefer fetched photos when present; otherwise keep discovery preview URLs.
    photos: fetchedPhotos.length > 0 ? fetchedPhotos : previewPhotos,
  };
}

export function OtherProfilePage() {
  const { uid } = useParams<{ uid: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser, currentUser } = useCurrentUser();
  const { user: profileUser, isLoading, isError, refetch } = useUser(uid);
  const { t } = useTranslation('common');
  const { startConversation } = useConversations(authUser);

  const preview =
    (location.state as ProfileLocationState | null)?.user?.uid === uid
      ? ((location.state as ProfileLocationState).user ?? null)
      : null;

  if (uid && currentUser?.uid === uid) {
    return <Navigate to="/account/my-profile" replace />;
  }

  const profileUserMerged = mergeProfile(profileUser, preview);

  if (isLoading && !profileUserMerged) {
    return <LoadingState />;
  }

  const onBack = () => {
    // location.key === 'default' means no in-app history (direct load/deep link).
    if (location.key === 'default') {
      navigate('/');
    } else {
      navigate(-1);
    }
  };

  if (isError) {
    return <ErrorState action={{ label: t('actions.retry'), onClick: () => void refetch() }} />;
  }

  if (!profileUserMerged) {
    return (
      <ErrorState
        title={t('notFound.title')}
        message={t('notFound.message')}
        action={{ label: t('actions.back'), onClick: onBack }}
      />
    );
  }

  const handleMessage = async () => {
    if (!authUser) return;
    try {
      const conversationId = await startConversation(profileUserMerged.uid);
      navigate(`/chat/${conversationId}`);
    } catch (err) {
      console.error('Dialogue creation failure:', err);
    }
  };

  return (
    <ProfilePage
      user={profileUserMerged}
      isOwn={false}
      onBack={onBack}
      onMessage={() => void handleMessage()}
    />
  );
}
